import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWaitlist } from "@/context/WaitlistContext";
import { getWaitlistCount } from "@/lib/api";
import { DualPhones } from "@/components/landing/PhoneMockups";

const CONTAINER_VARIANTS = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};
const PREVIEW_INITIAL = { opacity: 0, y: 20, scale: 0.98 };
const PREVIEW_ANIMATE = { opacity: 1, y: 0, scale: 1 };
const PREVIEW_TRANSITION = { duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] };
const AVATAR_COLORS = ["#4A90E2", "#50E3C2", "#6FB1F0"];

const Hero = () => {
  const { openWaitlist } = useWaitlist();
  const [count, setCount] = useState(null);

  const loadCount = useCallback(() => {
    getWaitlistCount()
      .then(setCount)
      .catch(() => {
        // Non-critical: leave count hidden if it can't be fetched.
      });
    // getWaitlistCount is a stable module import.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  const scrollToDemo = () => {
    const el = document.getElementById("live-demo");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden jb-noise" data-testid="hero-section">
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(900px circle at 12% 8%, rgba(74,144,226,0.16), transparent 55%), radial-gradient(760px circle at 88% 18%, rgba(80,227,194,0.15), transparent 55%)",
        }}
      />
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-4 sm:px-6 py-16 sm:py-24 lg:grid-cols-2">
        <motion.div variants={CONTAINER_VARIANTS} initial="hidden" animate="show">
          <motion.div variants={ITEM_VARIANTS}>
            <Badge className="mb-5 gap-1.5 rounded-full border border-[rgba(74,144,226,0.25)] bg-[rgba(74,144,226,0.08)] px-3 py-1 text-[var(--jb-blue)] hover:bg-[rgba(74,144,226,0.12)]" data-testid="hero-badge">
              <Sparkles className="h-3.5 w-3.5" /> Launching Soon
            </Badge>
          </motion.div>

          <motion.h1 variants={ITEM_VARIANTS} className="font-display text-4xl font-semibold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Meet JedBot,
            <br />
            <span style={{ background: "linear-gradient(120deg, #4A90E2, #50E3C2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              your intelligent AI companion.
            </span>
          </motion.h1>

          <motion.p variants={ITEM_VARIANTS} className="mt-5 max-w-[54ch] text-base leading-relaxed text-slate-600 sm:text-lg">
            From idea to answer in seconds. JedBot helps you plan, write, learn and get things done
            with real-time, personalized assistance — in any language, no coding required.
          </motion.p>

          <motion.div variants={ITEM_VARIANTS} className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={openWaitlist}
              size="lg"
              className="group rounded-xl bg-[var(--jb-blue)] px-6 text-white shadow-[0_10px_28px_rgba(74,144,226,0.3)] transition-transform transition-colors hover:-translate-y-0.5 hover:bg-[#3E7FCA] active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-[var(--jb-mint)] focus-visible:ring-offset-2"
              data-testid="hero-join-waitlist-button"
            >
              Join the Waitlist
              <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              onClick={scrollToDemo}
              size="lg"
              variant="secondary"
              className="rounded-xl border border-slate-200 bg-white px-6 text-slate-900 shadow-sm transition-transform transition-colors hover:-translate-y-0.5 hover:bg-slate-50 active:scale-[0.98]"
              data-testid="hero-try-demo-button"
            >
              <MessageSquare className="mr-1.5 h-4 w-4 text-[var(--jb-blue)]" /> Try the Live Demo
            </Button>
          </motion.div>

          <motion.div variants={ITEM_VARIANTS} className="mt-6 flex items-center gap-3 text-sm text-slate-500">
            <div className="flex -space-x-2">
              {AVATAR_COLORS.map((c) => (
                <span key={c} className="h-7 w-7 rounded-full border-2 border-white" style={{ background: c }} />
              ))}
            </div>
            <span data-testid="hero-waitlist-count">
              {count !== null ? `${count.toLocaleString()}+ people` : "Join the crowd"} on the waitlist
            </span>
          </motion.div>
        </motion.div>

        {/* Smartphone product preview */}
        <motion.div
          initial={PREVIEW_INITIAL}
          animate={PREVIEW_ANIMATE}
          transition={PREVIEW_TRANSITION}
          className="relative"
        >
          <DualPhones />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
